import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { isEmpty } from 'lodash'
// @ts-ignore
import { OpenInNewTab } from '@netbasequid/core'
import Domain from 'enums/Domain'
import { getDomainName } from 'utils/sourceUtil'
import { haveInstagramMetricsStore, haveYoutubeMetricsStore } from 'utils/mediaUtil'
import { Detail } from './types'
import { MediaDetailContext } from './context'
import Author from './Author'
import Date from './Date'
import DomainLink from './DomainLink'
import MediaMessage from './Message'
import UserTags from './UserTags'
import TwitterActions from './TwitterActions'
import ActionButton from './ActionButton'
import Divider from './Divider'
import ChannelMetrics from './ChannelMetrics'

const Container = styled.div``

const Header = styled.div`
  height: 48px;
  display: flex;
  margin-bottom: 8px;
`

const StyledAuthor = styled(Author)`
  flex: 1;
  overflow: hidden;
`

const StyledDate = styled(Date)`
  height: 20px;
  padding-top: 2px;
  margin-left: 8px;
`

const DomainLinkContainer = styled.div`
  padding-top: 2px;
  margin-left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledUserTags = styled(UserTags)`
  margin-top: 8px;
`

const Actions = styled.div`
  margin-top: 2px;
  margin-left: -6px;
  display: flex;
  align-items: center;
`

const VerticalDivider = styled(Divider)`
  margin-left: 10px;
  margin-right: 10px;
`

const HorizontalDivider = styled(Divider)`
  margin-top: 6px;
  margin-bottom: 12px;
`

type MediaDetailProps = {
  className?: string
  data: Detail
}

const MediaDetail = forwardRef<HTMLDivElement, MediaDetailProps>(({ className, data }, ref) => {
  const { t } = useTranslation()

  const hasUserTags = !isEmpty(data.photoUserTags)
  const isTwitterDomain = data.domain === Domain.TWITTER

  const isInvalidInstagramMetrics =
    data.domain === Domain.INSTAGRAM && !haveInstagramMetricsStore(data.timestamp)
  const isInvalidYoutubeMetrics =
    data.domain === Domain.YOUTUBE && !haveYoutubeMetricsStore(data.timestamp)
  const isInvalidMetrics = isInvalidInstagramMetrics || isInvalidYoutubeMetrics

  return (
    <MediaDetailContext.Provider value={{ ...data }}>
      <Container className={className} ref={ref}>
        <Header>
          <StyledAuthor />
          <StyledDate />
          <DomainLinkContainer>
            <DomainLink />
          </DomainLinkContainer>
        </Header>
        <MediaMessage />
        {hasUserTags && <StyledUserTags />}
        <Actions>
          {isTwitterDomain && (
            <>
              <TwitterActions />
              <VerticalDivider height="16px" />
            </>
          )}
          <ActionButton
            url={data.postUrl}
            IconComponent={OpenInNewTab}
            tooltipContent={data.domain && `${t('viewOn', { source: getDomainName(data.domain) })}`}
          />
        </Actions>
        {!isInvalidMetrics && (
          <>
            <HorizontalDivider width="276px" />
            <ChannelMetrics />
          </>
        )}
      </Container>
    </MediaDetailContext.Provider>
  )
})

export default MediaDetail
